import React from 'react';
import { observer } from 'mobx-react';
import { CustomInput, Row, Col } from 'reactstrap';
import './ReachTemplate.scss';
import { toTitleFromUnderscore } from 'cd-eft/utils/stringUtilities';

interface IReachTemplateProps {
    reachTemplateForm;
    formValidation;
    handleInputChange;
    handleOnBlur?
}

export const ReachTemplateContainer = observer((props: IReachTemplateProps) => {
    const {
        reachTemplateForm,
        formValidation,
        handleInputChange,
        handleOnBlur,

    } = props;
    return (
        <div className="reach-template-container">
            <div className="reach-template-details">
                <Row>
                    <Col xs={4}>
                        <div className="template-item">
                            <label className='usa-label' htmlFor="bottomWidth">
                                Bottom Width (ft)
                            </label>
                            <input
                                className={`usa-input ${formValidation.formErrors.bottomWidth} `}
                                id="bottomWidth"
                                name='bottomWidth'
                                value={reachTemplateForm.bottomWidth}
                                type="number"
                                onChange={handleInputChange}
                                onBlur={handleOnBlur}
                                step={0.01}
                                min={0}
                            />
                        </div>
                    </Col>
                    <Col xs={4}>
                        <div className="template-item">
                            <label className='usa-label' htmlFor="frontHeight">
                                Front Height (ft)
                            </label>
                            <input
                                className={`usa-input ${formValidation.formErrors.frontHeight}`}
                                id="frontHeight"
                                name="frontHeight"
                                value={reachTemplateForm.frontHeight}
                                type="number"
                                onChange={handleInputChange}
                                onBlur={handleOnBlur}
                                step={0.01}
                                min={0}
                            />
                        </div>
                    </Col>
                    <Col xs={4}>
                        <div className="template-item">
                            <label className='usa-label' htmlFor="topWidth">
                                Top Width (ft)
                            </label>
                            <input
                                className={`usa-input ${formValidation.formErrors.topWidth} `}
                                id="topWidth"
                                name="topWidth"
                                value={reachTemplateForm.topWidth}
                                type="number"
                                onChange={handleInputChange}
                                onBlur={handleOnBlur}
                                step={0.01}
                                min={0}
                            />
                        </div>
                    </Col>
                </Row>
            </div>
            <div className="reach-template-slope-details">
                <Row>
                    <Col xs={4}>
                        <span >Cut Slope </span>
                        <div className="template-slope-item">
                            <label className='usa-label' htmlFor="cutSlopeMode">
                                Mode
                            </label>
                            <select
                                className="usa-select"
                                id="cutSlopeMode"
                                name="cutSlopeMode"
                                value={reachTemplateForm.slopeLimits[0].slopeMode}
                                onChange={handleInputChange}
                            >
                                <option value={'WIDTH_BASED'}>Width Based</option>
                                <option value={'RATIO_BASED'}>Ratio Based</option>
                            </select>
                        </div>
                        <div className="template-slope-item">
                            <label className='usa-label' htmlFor="cutSlopeValue">
                                Value {reachTemplateForm.slopeLimits[0].slopeMode === 'RATIO_BASED' ? '(ft/ft)':'(ft)'}
                            </label>
                            <input
                                className={`usa-input ${formValidation.formErrors.cutSlopeValue} `}
                                id="cutSlopeValue"
                                name="cutSlopeValue"
                                value={reachTemplateForm.slopeLimits[0].slopeValue}
                                type="number"
                                onChange={handleInputChange}
                                onBlur={handleOnBlur}
                                step={0.01}
                                min={0}
                            />
                        </div>
                        <div className="template-slope-item">
                            <label
                                className={`${reachTemplateForm.slopeLimits[0].isLimitEnforced ? 'select-label' : 'select-label-unchecked'}`}
                                htmlFor="enableCutSlope"
                            >
                                {reachTemplateForm.slopeLimits[0].isLimitEnforced ? 'Cut Slope Enabled' : 'Enable Cut Slope'}
                            </label>
                            <CustomInput
                                id="enableCutSlope"
                                name="enableCutSlope"
                                type="switch"
                                className={`checkbox-input-switch${reachTemplateForm.slopeLimits[0].isLimitEnforced ? ' selected' : ''}`}
                                bsSize="lg"
                                checked={!!reachTemplateForm.slopeLimits[0].isLimitEnforced}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="template-slope-item">
                            <label className={`usa-label ${!reachTemplateForm.slopeLimits[0].isLimitEnforced ? 'disabled-text':''}`} htmlFor="cutSlopeLimitingMode">
                                Limiting Mode
                            </label>
                            <input
                                className={`usa-input limiting-mode ${!reachTemplateForm.slopeLimits[0].isLimitEnforced ? 'disabled-text':''}`}
                                id="cutSlopeLimitingMode"
                                name="cutSlopeLimitingMode"
                                value={reachTemplateForm.slopeLimits[0].limitingMode ? toTitleFromUnderscore(reachTemplateForm.slopeLimits[0].limitingMode) : ''}
                                type="text"
                                onChange={handleInputChange}
                                onBlur={handleOnBlur}
                                readOnly
                            />
                        </div>
                        <div className="template-slope-item">
                            <label className={`usa-label ${!reachTemplateForm.slopeLimits[0].isLimitEnforced ? 'disabled-text':''}`} htmlFor="cutSlopeLimit">
                                Limit {reachTemplateForm.slopeLimits[0].slopeMode === 'RATIO_BASED' ? '(ft)':'(ft/ft)'}
                            </label>
                            <input
                                className={`usa-input ${!reachTemplateForm.slopeLimits[0].isLimitEnforced ? 'disabled-text': formValidation.formErrors.cutSlopeLimit}`}
                                id="cutSlopeLimit"
                                name="cutSlopeLimit"
                                value={reachTemplateForm.slopeLimits[0].slopeLimit}
                                type="number"
                                onChange={handleInputChange}
                                onBlur={handleOnBlur}
                                disabled={!reachTemplateForm.slopeLimits[0].isLimitEnforced}
                                step={0.01}
                                min={0}
                            />
                        </div>
                    </Col>
                    <Col xs={4}>
                        <span className="bold-headers">Front Slope</span>
                        <div className="template-slope-item">
                            <label className='usa-label' htmlFor="frontSlopeMode">
                                Mode
                            </label>
                            <select
                                className="usa-select"
                                id="frontSlopeMode"
                                name="frontSlopeMode"
                                value={reachTemplateForm.slopeLimits[1].slopeMode}
                                onChange={handleInputChange}
                            >
                                <option value={'WIDTH_BASED'}>Width Based</option>
                                <option value={'RATIO_BASED'}>Ratio Based</option>
                            </select>
                        </div>
                        <div className="template-slope-item">
                            <label className='usa-label' htmlFor="frontSlopeValue">
                                Value {reachTemplateForm.slopeLimits[1].slopeMode === 'RATIO_BASED' ? '(ft/ft)':'(ft)'}
                            </label>
                            <input
                                className={`usa-input ${formValidation.formErrors.frontSlopeValue}`}
                                id="frontSlopeValue"
                                name="frontSlopeValue"
                                value={reachTemplateForm.slopeLimits[1].slopeValue}
                                type="number"
                                onChange={handleInputChange}
                                onBlur={handleOnBlur}
                                step={0.01}
                                min={0}
                            />
                        </div>
                        <div className="template-slope-item">
                            <label
                                className={`${reachTemplateForm.slopeLimits[1].isLimitEnforced ? 'select-label' : 'select-label-unchecked'
                                    }`}
                                htmlFor="enableFrontSlope"
                            >
                                {reachTemplateForm.slopeLimits[1].isLimitEnforced ? 'Front Slope Enabled':'Enable Front Slope'}
                            </label>
                            <CustomInput
                                id="enableFrontSlope"
                                name="enableFrontSlope"
                                type="switch"
                                className={`checkbox-input-switch${reachTemplateForm.slopeLimits[1].isLimitEnforced ? ' selected' : ''
                                    }`}
                                bsSize="lg"
                                checked={!!reachTemplateForm.slopeLimits[1].isLimitEnforced}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="template-slope-item">
                            <label className={`usa-label ${!reachTemplateForm.slopeLimits[1].isLimitEnforced ? 'disabled-text':''}`} htmlFor="frontSlopeLimitingMode">
                                Limiting Mode
                            </label>
                            <input
                                className={`usa-input limiting-mode ${!reachTemplateForm.slopeLimits[1].isLimitEnforced ? 'disabled-text':''}`}
                                id="frontSlopeLimitingMode"
                                value={reachTemplateForm.slopeLimits[1].limitingMode ? toTitleFromUnderscore(reachTemplateForm.slopeLimits[1].limitingMode) : ''}
                                type="text"
                                onChange={handleInputChange}
                                readOnly
                            />
                        </div>
                        <div className="template-slope-item">
                            <label className={`usa-label ${!reachTemplateForm.slopeLimits[1].isLimitEnforced ? 'disabled-text':''}`} htmlFor="frontSlopeLimit">
                                Limit {reachTemplateForm.slopeLimits[1].slopeMode === 'RATIO_BASED' ? '(ft)':'(ft/ft)'}
                            </label>
                            <input
                                className={`usa-input ${!reachTemplateForm.slopeLimits[1].isLimitEnforced ? 'disabled-text':formValidation.formErrors.frontSlopeLimit}`}
                                id="frontSlopeLimit"
                                name="frontSlopeLimit"
                                value={reachTemplateForm.slopeLimits[1].slopeLimit}
                                type="number"
                                onChange={handleInputChange}
                                onBlur={handleOnBlur}
                                disabled={!reachTemplateForm.slopeLimits[1].isLimitEnforced}
                                step={0.01}
                                min={0}
                            />
                        </div>
                    </Col>
                    <Col xs={4}>
                        <span className="bold-headers">Back Slope</span>
                        <div className="template-slope-item">
                            <label className='usa-label' htmlFor="backSlopeMode">
                                Mode
                            </label>
                            <select
                                className="usa-select"
                                id="backSlopeMode"
                                name="backSlopeMode"
                                value={reachTemplateForm.slopeLimits[2].slopeMode}
                                onChange={handleInputChange}
                            >
                                <option value={'WIDTH_BASED'}>Width Based</option>
                                <option value={'RATIO_BASED'}>Ratio Based</option>
                            </select>
                        </div>
                        <div className="template-slope-item">
                            <label className='usa-label' htmlFor="backSlopeValue">
                                Value {reachTemplateForm.slopeLimits[2].slopeMode === 'RATIO_BASED' ? '(ft/ft)':'(ft)'}
                            </label>
                            <input
                                className={`usa-input ${formValidation.formErrors.backSlopeValue}`}
                                id="backSlopeValue"
                                name="backSlopeValue"
                                value={reachTemplateForm.slopeLimits[2].slopeValue}
                                type="number"
                                onChange={handleInputChange}
                                onBlur={handleOnBlur}
                                step={0.01}
                                min={0}
                            />
                        </div>
                        <div className="template-slope-item">
                            <label
                                className={`${reachTemplateForm.slopeLimits[2].isLimitEnforced ? 'select-label' : 'select-label-unchecked'
                                    }`}
                                htmlFor="enableBackSlope"
                            >
                                {!reachTemplateForm.slopeLimits[2].isLimitEnforced ? 'Enable Back Slope' : 'Back Slope Enabled'}
                            </label>
                            <CustomInput
                                id="enableBackSlope"
                                name="enableBackSlope"
                                type="switch"
                                className={`checkbox-input-switch${reachTemplateForm.slopeLimits[2].isLimitEnforced ? ' selected' : ''
                                    }`}
                                bsSize="lg"
                                checked={!!reachTemplateForm.slopeLimits[2].isLimitEnforced}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="template-slope-item">
                            <label className={`usa-label ${!reachTemplateForm.slopeLimits[2].isLimitEnforced ? 'disabled-text':''}`} htmlFor="backSlopeLimitingMode">
                                Limiting Mode
                            </label>
                            <input
                                className={`usa-input limiting-mode ${!reachTemplateForm.slopeLimits[2].isLimitEnforced ? 'disabled-text':''}`}
                                id="backSlopeLimitingMode"
                                value={reachTemplateForm.slopeLimits[2].limitingMode ? toTitleFromUnderscore(reachTemplateForm.slopeLimits[2].limitingMode) : ""}
                                type="text"
                                onChange={handleInputChange}
                                onBlur={handleOnBlur}
                                readOnly
                            />
                        </div>
                        <div className="template-slope-item">
                            <label className={`usa-label ${!reachTemplateForm.slopeLimits[2].isLimitEnforced ? 'disabled-text':''}`} htmlFor="backSlopeLimit">
                                Limit {reachTemplateForm.slopeLimits[2].slopeMode === 'RATIO_BASED' ? '(ft)':'(ft/ft)'}
                            </label>
                            <input
                                className={`usa-input ${!reachTemplateForm.slopeLimits[2].isLimitEnforced ? 'disabled-text': formValidation.formErrors.backSlopeLimit}`}
                                id="backSlopeLimit"
                                name="backSlopeLimit"
                                value={reachTemplateForm.slopeLimits[2].slopeLimit}
                                type="number"
                                onChange={handleInputChange}
                                onBlur={handleOnBlur}
                                disabled={!reachTemplateForm.slopeLimits[2].isLimitEnforced}
                                min={0}
                                step={0.01}
                            />
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
});